import {FC, useEffect, useState} from "react"
import {Grid, Box, styled, Paper, Stack, useTheme} from "@mui/material"
import {thoughtPiecesEndpoints} from "../../services/api/thought-pieces"
import {ThoughtPiecesModel} from "../../types/ThoughtPiecesModel"
import {useNavigate, useParams} from "react-router-dom"
import useSelectedThoughtPiecesStore from "../../stores/fullPageThoughtPiecesStore"
import useHomepageTabStore from "../../stores/homepageTabStore"
import {
  BackButton,
  ArticleBanner,
  ArticleBody,
  ArticleDetails,
} from "../../components/molecules/thoughtPiecesFullpage"
import {convertThoughtPiecesToCarouselItemList} from "../../helpers/parserHelper"
import {useConfigProvider} from "../../config"
import {CarouselItemProps} from "../../components/molecules/CarouselItem/types"
import Carousel from "../../components/organisms/Carousel/Carousel"
import useHomeNavigation from "../../hooks/useHomeNavigation"
import {appInsights} from "../../config/appInsights"
import {fabricEndpoints} from "../../services"
import {useMutation} from "@tanstack/react-query"
import {decodeToken} from "react-jwt"
import {Token} from "../../types"
import {useSessionStorage} from "usehooks-ts"

declare global {
  interface Window {
    cdAnalytics?: {
      trackPage: () => void
      va: string
    }
  }
}

const FullPage: FC = () => {
  const {type, name, subtype} = useParams()
  const {id, setId} = useSelectedThoughtPiecesStore()
  const {setCurrentTab} = useHomepageTabStore()
  const theme = useTheme()
  const {getThoughtPiecesPage, getThoughtPiecesDiscoverNext} =
    thoughtPiecesEndpoints()
  const {sendFabricEvents} = fabricEndpoints()
  const {cmsImageUrl, token} = useConfigProvider()
  const [fullItem, setFullItem] = useState<ThoughtPiecesModel>()
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [carouselItemList, setCarouselItemList] = useState<CarouselItemProps[]>(
    [],
  )
  const [carouselLoading, setCarouselLoading] = useState<boolean>(true)
  const [hasCarouselError, setCarouselHasError] = useState<boolean>(false)
  const {
    addNavigationHistory,
    getNavigationPrev,
    getNavigationObject,
    removeNavigationHistory,
  } = useHomeNavigation()
  const [isBackRoot, setIsBackRoot] = useState<boolean>(false)
  const navigate = useNavigate()

  const sendFabricEventsMutation = useMutation({
    mutationFn: sendFabricEvents,
    mutationKey: ["sendFabricEvents"],
  })

  const Item = styled(Paper)(({theme}) => ({
    backgroundColor: "#d3d3d3",
    ...theme.typography.body2,
    padding: theme.spacing(0),
    textAlign: "center",
    color: theme.palette.text.secondary,
  }))

  const handleBackClick = async () => {
    let navList = getNavigationObject()
    navList.historyNavigation.length <= 2 &&
      setCurrentTab(navList.historyNavigation[0])
    setIsBackRoot(navList.historyNavigation.length <= 2)
    await removeNavigationHistory()
    if (navList.historyNavigation.length <= 2) {
      navigate("/")
    } else {
      navigate(-1)
    }
  }

  const handleAddNavigation = async (page: string) => {
    let navList = getNavigationObject()
    navList.historyNavigation.length === 0 &&
      (await addNavigationHistory("Overview"))
    await addNavigationHistory(page)
  }

  const handleLoadPage = async () => {
    await getThoughtPiecesPage(type, subtype, name, id).then(
      async (data: ThoughtPiecesModel) => {
        setFullItem(data)
        setIsFetching(false)

        const decodedToken = decodeToken<Token>(token)

        await sendFabricEventsMutation.mutateAsync([
          {
            type: "view_thoughtpiece",
            targetId: id,
            targetName: data?.title,
            appUrl: window.location.href,
          },
        ])

        appInsights.trackEvent({
          name: "VIEW_THOUGHTPIECE",
          properties: {
            user_id: decodedToken?.sub,
            event: "view_thoughtpiece",
            target_id: id,
            target_name: data?.title,
          },
        })

        if (
          typeof window.cdAnalytics !== "undefined" &&
          typeof window.cdAnalytics.trackPage === "function"
        ) {
          var contactEmail = decodedToken?.email
          if (typeof contactEmail !== "undefined") {
            Object.assign(window.cdAnalytics.va, {_clde: contactEmail})
            window.cdAnalytics.va = "_clde=" + contactEmail
          }
          window.cdAnalytics.trackPage()
        }
        var scriptTag = document.querySelectorAll(
          `script[src*="https://analytics-eu.clickdimensions.com/tsr.js"]`,
        )
        if (scriptTag) {
          scriptTag.forEach((script) => script.remove())
        }
      },
    )
  }
  const [language] = useSessionStorage("language", "en")

  const handleLoadCarouselData = async () => {
    let podcastRows = 16
    let analysisRows = 5
    setCarouselHasError(false)
    setCarouselLoading(true)
    await getThoughtPiecesDiscoverNext(
      type,
      fullItem?.group,
      podcastRows,
      analysisRows,
    )
      .then((data) => {
        if (data) {
          setCarouselItemList([
            ...convertThoughtPiecesToCarouselItemList(
              data,
              cmsImageUrl,
              language,
            ),
          ])
          setCarouselLoading(false)
        }
      })
      .catch((error) => {
        setCarouselHasError(true)
      })
  }

  useEffect(() => {
    if (fullItem?.group) {
      setId(fullItem?.group)
      handleAddNavigation(fullItem?.title)
      handleLoadCarouselData()
    }
  }, [fullItem?.group, language])

  useEffect(() => {
    setCurrentTab("Discover")
    handleLoadPage()
  }, [])

  return (
    <Stack
      component="article"
      direction="column"
      width="100%"
      height="auto"
      gap="0.25rem"
      paddingY="1rem"
      bgcolor={theme.palette.common.white}
      border="1px solid rgba(238, 238, 238)"
      borderRadius="4px"
      boxShadow={"rgb(238, 238, 238) 0px 4px 4px 0px"}
      marginBottom="29px"
    >
      <Box sx={{flexGrow: 1}}>
        <Grid container spacing={0} component="section">
          <Grid size={12} paddingX="16px" paddingBottom="16px" component="nav" id="article-navigation">
            <BackButton
              page={getNavigationPrev()}
              onClick={handleBackClick}
              backToRoot={isBackRoot}
            />
          </Grid>
          <Grid size={12} paddingBottom="24px" component="section" id="article-banner">
            <ArticleBanner
              imageUrl={fullItem?.imageLarge}
              parameters={fullItem?.renderingParameters}
              title={fullItem?.title}
              loading={isFetching}
            />
          </Grid>
          <Grid size={12} paddingX="16px" paddingBottom="16px" component="section" id="article-details">
            <ArticleDetails
              articleTitle={fullItem?.title}
              title={fullItem?.articleTagTitle}
              date={fullItem?.date}
              authors={fullItem?.authors}
              tags={fullItem?.tagTitles}
              loading={isFetching}
            />
          </Grid>
          <Grid size={12} paddingX="16px" paddingBottom="16px" component="section" id="article-body">
            <ArticleBody
              textBlocks={fullItem?.textBlockValues}
              type={type}
              loading={isFetching}
            />
          </Grid>
          <Grid size={12} paddingX="16px" sx={{margin: "0px 5.5px"}} component="section" id="discover-next">
            <Item
              style={{
                padding: "16px 8px",
                margin: "10px",
                background: "#FFFFFF",
                boxShadow: "rgb(238, 238, 238) 0 4px 4px 0",
                borderRadius: "8px",
                borderColor: "#f4f4f4",
                border: "solid 1px rgb(244, 244, 244",
              }}
            >
              {
                <Carousel
                  title="Discover next"
                  dataSource={carouselItemList}
                  style={{
                    mainStyle: {
                      columnGap: 1,
                    },
                    titleStyle: {
                      color: "#242d35",
                      fontSize: 16,
                      fontStyle: "Open Sans",
                      fontWeight: 600,
                    },
                  }}
                  isLoading={carouselLoading}
                  isLoadingError={hasCarouselError}
                  isError={hasCarouselError}
                  isFetching={carouselLoading}
                  isRefetching={carouselLoading}
                  isRefetchError={hasCarouselError}
                  refetch={handleLoadCarouselData}
                />
              }
            </Item>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  )
}
FullPage.displayName = "FULL PAGE"

export default FullPage
